import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import { withStyles } from '@mui/styles'

import { expansionPanelStyles } from './expansion-panel.styles'

/**
 * @typedef {object} AccordionComponentProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {'left' | 'right'} [triggerPosition='left'] - La alineación horizontal del texto de acción "Ver más/Ver menos".
 * @property {boolean} [isExpanded=false] - Define el estado inicial del acordeón (expandido o contraído).
 * @property {React.ReactNode} summary - El contenido principal que se muestra en la sección de resumen (la cabecera) del acordeón.
 * @property {React.ReactNode} [summaryExtra] - Contenido adicional opcional que se muestra en la sección de resumen.
 * @property {React.ReactNode} details - El contenido detallado que se revela cuando el acordeón está expandido.
 * @property {React.ReactNode} [footer] - Contenido opcional que se renderiza fuera y debajo del componente de acordeón.
 */

/**
 * Un componente de acordeón reutilizable y personalizable que gestiona su propio estado de expansión.
 *
 * Está construido sobre el componente `Accordion` de Material-UI y permite una composición flexible
 * de su contenido a través de props (`summary`, `details`, `footer`, etc.). El texto del disparador
 * para expandir/contraer está internacionalizado usando `react-intl`.
 *
 * @component
 * @example
 * // Se necesita un <IntlProvider> en un nivel superior con los siguientes mensajes definidos:
 * // 'pages.budget.view.panel1.summary.expanded.true': 'Ver menos',
 * // 'pages.budget.view.panel1.summary.expanded.false': 'Ver más'
 *
 * const MyComponent = () => (
 * <AccordionComponent
 * isExpanded={true}
 * summary={<Typography variant="h6">Título de la Sección</Typography>}
 * summaryExtra={<Typography variant="caption">Detalles adicionales en el resumen</Typography>}
 * details={
 * <div>
 * <p>Este es el contenido que se muestra cuando el panel está expandido.</p>
 * </div>
 * }
 * footer={<button>Botón en el Pie de Página</button>}
 * />
 * );
 *
 * @param {AccordionComponentProps} props - Las props del componente AccordionComponent.
 */
export const AccordionComponent = withStyles(expansionPanelStyles)(({
  classes,
  triggerPosition = 'left',
  isExpanded = false,
  ...props
}) => {
  const { formatMessage } = useIntl()

  const [expanded, setExpanded] = useState(isExpanded)

  return (
    <>
      <Accordion
        className={classes.accordion}
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
      >
        <AccordionSummary>
          <Box mb={2} display="flex">
            {props.summary}
            <Box flexGrow={1} textAlign={triggerPosition}>
              <Typography variant="body2" color="error">
                {formatMessage({
                  id: `pages.budget.view.panel1.summary.expanded.${expanded.toString()}`,
                })}
              </Typography>
            </Box>
          </Box>
          <Divider light />
          <Box mt={2} display="flex" alignItems="center" flexWrap="wrap">
            {props.summaryExtra && <Box flexGrow={1}>{props.summaryExtra}</Box>}
          </Box>
        </AccordionSummary>
        <AccordionDetails>{props.details}</AccordionDetails>
      </Accordion>
      {props.footer && (
        <Box ml={2} mr={2}>
          {props.footer}
        </Box>
      )}
    </>
  )
})
